<template>
	<!-- HubLogo with unreadmessages marker -->
	<div
		v-if="hub"
		class="group relative z-0 block h-full w-full cursor-pointer rounded-xl text-center transition-all ease-in-out"
		:title="hub?.name"
		:data-rail-active="active || undefined"
	>
		<!-- v-if, not v-show: a hidden iframe still loads miniclient.html and runs a full
		     MatrixClient and sliding sync of its own, so it has to not exist to cost nothing. -->
		<div
			v-if="showMiniclient"
			class="-top-050 -right-050 absolute z-10"
		>
			<iframe
				:id="miniClientId + '_' + hubId"
				class="pointer-events-none h-300 w-300 border-none"
				:src="miniclientSrc"
				aria-hidden="true"
				tabindex="-1"
			/>
		</div>

		<!-- Inactive hubs are dimmed so the active one stands out; they brighten on hover to read as switch targets. -->
		<HubIcon
			class="transition-opacity duration-200"
			:class="active ? '' : 'opacity-60 group-hover:opacity-100'"
			:hub-name="hub.name"
			:icon-url="hub.iconUrlLight"
			:icon-url-dark="hub.iconUrlDark"
			:is-active="active"
		/>
	</div>
</template>

<script lang="ts" setup>
	// Packages
	import { computed, ref, watch } from 'vue';

	// Components
	import HubIcon from '@hub-client/components/ui/HubIcon.vue';

	// Composables
	import { useMiniclientStartSlot } from '@global-client/composables/miniclientGate.composable';

	// Logic
	import { cacheBust } from '@global-client/logic/utils/cacheBust';

	// Stores
	import { useGlobal } from '@global-client/stores/global';
	import { useHubs } from '@global-client/stores/hubs';

	import { miniClientId, useMessageBox } from '@hub-client/stores/messagebox';
	import { FeatureFlag, useSettings } from '@hub-client/stores/settings';

	type Props = {
		type?: string;
		size?: string;
		hubId: string;
		pinned?: boolean;
		pinnable?: boolean;
		active?: boolean;
	};

	const props = withDefaults(defineProps<Props>(), {
		type: 'circle',
		size: 'xl',
		hubId: '',
		pinned: false,
		pinnable: false,
		active: false,
	});
	const global = useGlobal();
	const messagebox = useMessageBox();
	const settings = useSettings();
	const hubs = useHubs();

	const hub = computed(() => hubs.hub(props.hubId));

	// Kept as the object rather than a string: getAuthInfo returns null when this hub has no
	// token yet, and JSON.stringify(null) is 'null' — truthy, which would gate nothing.
	const authInfo = ref(global.getAuthInfo(props.hubId));

	// A miniclient is only worth its own MatrixClient once it can authenticate and its badge is
	// actually wanted. Handed to the gate so this hub only takes a turn in the start queue while it
	// could use one — a hub still waiting for its token would otherwise spend a turn on nothing.
	const canStartMiniclient = computed(() => !!hub.value && !!authInfo.value && settings.isFeatureEnabled(FeatureFlag.unreadCounter));

	const mayStartMiniclient = useMiniclientStartSlot(canStartMiniclient);

	// ...and only once it is not about to compete with a hub the user is opening.
	const showMiniclient = computed(() => mayStartMiniclient.value && canStartMiniclient.value);

	const miniclientSrc = computed(
		() => `${hub.value?.url}/miniclient.html?${new URLSearchParams({ accessToken: JSON.stringify(authInfo.value), hubId: props.hubId, cb: cacheBust })}`,
	);

	// setupMiniclient awaits a handshake with the miniclient iframe, so running it while that
	// iframe is not mounted would leave it waiting on a frame that never appears. Its callbacks
	// are registered per message type and overwrite, so re-running it on a later toggle is safe.
	watch(
		showMiniclient,
		(show) => {
			if (show) void hubs.setupMiniclient(props.hubId);
		},
		{ immediate: true },
	);

	// When a user opens the hub for the first time on a device or in a browser, the accessToken
	// and userId are only stored after the receivedMessage action with a message of type addAuthInfo
	// from the messageBox is finished.
	messagebox.$onAction(({ name, args, after }) => {
		if (name === 'receivedMessage' && args[0].type === 'addAuthInfo') {
			after(() => {
				authInfo.value = global.getAuthInfo(props.hubId);
			});
		}
	});
</script>
